'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import clsx from "clsx";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, A11y } from "swiper/modules";
import 'swiper/css';
import 'swiper/css/navigation';
import '@/styles/artworkmodal.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faBookmark,
  faCartShopping,
  faCrown,
  faDownload,
  faCheckCircle,
  faCheck,
  faGift,
  faHeart,
  faShareNodes,
  faWandMagicSparkles,
  faXmark,
  faFileLines,
  faRocket
} from "@fortawesome/free-solid-svg-icons";
import { createPortal } from 'react-dom';
import { fetchCollections, type Collection } from "@/lib/collections";
import { ArtworkHoverOverlay } from "@/components/ui/ArtworkHoverOverlay";
import { UnifiedCheckoutModal } from "@/components/payments/UnifiedCheckoutModal";
import { formatFileSize } from "@/lib/utils";
import { useArtworkInteractions } from "@/hooks/useArtworkInteractions";
import { useRouter } from "next/navigation";
import { authHelpers, apiFetch, apiRoutes } from "@/lib/api";
import { useQuickEditor } from "@/context/QuickEditorContext";
import { useDownloadCheck } from "@/hooks/useDownloadCheck";
import DownloadLimitReachedModal from "@/components/billing/DownloadLimitReachedModal";
import SubscriptionRequiredModal from "@/components/billing/SubscriptionRequiredModal";
import { useUser } from "@/context/UserContext";
import { isPremiumPlan } from "@/constants/plans";
import { Switch } from "@/components/ui/Switch";
import { useEditorPreferences } from "@/hooks/useEditorPreferences";

export type ArtworkModalItem = {
  id: string;
  title: string;
  image: string;
  format: string;
  isPremium?: boolean;
  isFree?: boolean;
  fileSize?: number | null;
  dominantColor?: string;
  fileName?: string | null;
  downloadUrl?: string | null;
  /** Tags reais da arte vindas do banco de dados */
  tags?: string[];
  /** Slug da coleção à qual a arte pertence (se houver) */
  collectionSlug?: string | null;
};

interface ArtworkModalProps {
  artwork: ArtworkModalItem;
  relatedArtworks: ArtworkModalItem[];
  onClose: () => void;
  onSwitchArtwork: (artwork: ArtworkModalItem) => void;
}

type FormatMetadata = {
  size: string;
  editorHint: string;
  qualityLabel: string;
  tags: string[];
};

type KnownFormat = "PSD" | "PNG" | "Canva" | "3D" | "AI" | "JPG";

const FORMAT_METADATA: Record<KnownFormat, FormatMetadata> = {
  PSD: {
    size: "64 MB",
    editorHint: "no Adobe Photoshop ou Photopea",
    qualityLabel: "Alta qualidade 6K",
    tags: ["Photoshop", "Layers editáveis", "Social media"]
  },
  PNG: {
    size: "18 MB",
    editorHint: "em editores como Adobe Photoshop ou Figma",
    qualityLabel: "Compressão otimizada",
    tags: ["Transparente", "Rapidez", "Feed & Stories"]
  },
  JPG: {
    size: "8 MB",
    editorHint: "em qualquer editor de imagens",
    qualityLabel: "Compressão otimizada",
    tags: ["Universal", "Rapidez", "Feed & Stories"]
  },
  Canva: {
    size: "Template 1-click",
    editorHint: "diretamente no Canva",
    qualityLabel: "Edição colaborativa",
    tags: ["Canva", "Customizável", "Time colaborativo"]
  },
  "3D": {
    size: "120 MB",
    editorHint: "em suites 3D como Blender ou Cinema 4D",
    qualityLabel: "Render 4K",
    tags: ["Render", "Depth", "Motion ready"]
  },
  AI: {
    size: "52 MB",
    editorHint: "no Adobe Illustrator",
    qualityLabel: "Vetorial escalável",
    tags: ["Illustrator", "Vetor", "Branding"]
  }
};

const TRANSITION_DURATION = 240;

export function ArtworkModal({ artwork, relatedArtworks, onClose, onSwitchArtwork }: ArtworkModalProps) {
  const [isMobile, setIsMobile] = useState(false);
  const [isEntering, setIsEntering] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [checkoutTarget, setCheckoutTarget] = useState<{ kind: "one_time"; productId: string; productTitle: string; amount: number; currency: string } | null>(null);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [limitReachedData, setLimitReachedData] = useState<{ hours: number; minutes: number; seconds: number } | undefined>();
  const [showAllTags, setShowAllTags] = useState(false);
  const [visibleTagsCount, setVisibleTagsCount] = useState(3);
  const [oneTimeCheckoutAmount, setOneTimeCheckoutAmount] = useState(6.99); // Default fallback
  const [collections, setCollections] = useState<Collection[]>([]);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [subscriptionModalOrigin, setSubscriptionModalOrigin] = useState<'crie_oferta' | 'direct' | null>(null);
  const [collectionArtworks, setCollectionArtworks] = useState<ArtworkModalItem[]>([]);
  const [loadingCollectionArtworks, setLoadingCollectionArtworks] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const pendingArtworkRef = useRef<ArtworkModalItem | null>(null);
  const tagsContainerRef = useRef<HTMLDivElement | null>(null);
  const { openEditor } = useQuickEditor();
  const router = useRouter();
  const { checkPermission, timeUntilReset } = useDownloadCheck();
  const { user, refresh: refreshUser } = useUser();

  // Usuário com assinatura ativa (Artes Turbo / CrieOferta / Admin Premium)
  // não deve ver a opção de comprar arte individual.
  const hasActiveSubscription = !!(user && (user.isPremium || isPremiumPlan(user.currentPlanCode)));
  
  // Editor preferences - persisted to database
  const { preferences, updatePreferences } = useEditorPreferences();
  const applyBrandColorsOnOpen = preferences.color;

  // Fetch collections from API
  useEffect(() => {
    fetchCollections().then((result) => setCollections(result.collections)).catch(console.error);
  }, []);

  // Fetch artworks from the same collection if the current artwork belongs to one
  useEffect(() => {
    const fetchCollectionArtworks = async () => {
      if (!artwork.collectionSlug) {
        setCollectionArtworks([]);
        return;
      }

      setLoadingCollectionArtworks(true);
      try {
        const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:4001/api";
        const response = await fetch(`${API_URL}/collections/${artwork.collectionSlug}`);
        
        if (response.ok) {
          const collection = await response.json();
          const artworks = collection.artworks || [];
          
          // Map to ArtworkModalItem format and exclude current artwork
          const mappedArtworks: ArtworkModalItem[] = artworks
            .filter((art: any) => art.id !== artwork.id)
            .map((art: any) => ({
              id: art.id,
              title: art.title,
              image: art.thumbnailUrl || art.image,
              format: art.format?.toUpperCase() || 'PSD',
              isPremium: art.isPremium ?? !art.isFree,
              isFree: art.isFree,
              fileSize: art.fileSize ? parseInt(art.fileSize) : null,
              tags: Array.isArray(art.tags) ? art.tags : [],
              collectionSlug: art.collectionSlug || artwork.collectionSlug
            }));
          
          setCollectionArtworks(mappedArtworks);
        } else {
          console.warn("Failed to fetch collection artworks");
          setCollectionArtworks([]);
        }
      } catch (error) {
        console.warn("Error fetching collection artworks:", error);
        setCollectionArtworks([]);
      } finally {
        setLoadingCollectionArtworks(false);
      }
    };

    fetchCollectionArtworks();
  }, [artwork.id, artwork.collectionSlug]);

  // Fetch artwork pricing from API whenever the modal shows a (possibly different) artwork
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:4001/api";
        const response = await fetch(`${API_URL}/public/products/single-artwork`);
        if (response.ok) {
          const product = await response.json();
          // product.amount may come as a string from the API (numeric type maps to string), coerce to Number
          const amount = Number(product.amount);
          if (!Number.isNaN(amount)) {
            setOneTimeCheckoutAmount(amount);
          } else {
            console.warn("Product amount is invalid, keeping fallback", product.amount);
          }
        } else {
          console.warn("Failed to fetch product pricing, using default");
        }
      } catch (error) {
        console.warn("Error fetching product pricing:", error);
      }
    };

    // Refetch pricing when the displayed artwork changes (modal reopened or swapped)
    fetchPricing();
  }, [artwork.id]);
  
  // Handler to update color preference in database
  const handleToggleBrandColor = useCallback(async (enabled: boolean) => {
    await updatePreferences({ color: enabled });
  }, [updatePreferences]);

  // Helper function to check authentication and redirect if needed
  const requireAuth = useCallback(() => {
    if (!authHelpers.isAuthenticated()) {
      // Use window.location.href to avoid client-side navigation race conditions
      window.location.href = '/login';
      return false;
    }
    return true;
  }, []);

  // Use the same hook as ArtworkHoverOverlay for synchronized state
  const { isLiked, isSaved, toggleLike, toggleSave, loading: interactionsLoading } = useArtworkInteractions(artwork.id);

  const normalizedFormat = useMemo(() => {
    const raw = artwork.format?.toUpperCase?.() ?? "PNG";
    return (raw in FORMAT_METADATA ? raw : "PNG") as KnownFormat;
  }, [artwork.format]);

  const downloadUrl = useMemo(() => {
    if (artwork.downloadUrl) {
      return artwork.downloadUrl;
    }
    const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:4001/api";
    const baseUrl = API_URL.replace(/\/api$/, "");
    return `${baseUrl}/api/artworks/${artwork.id}/download`;
  }, [artwork.downloadUrl, artwork.id]);

  const metadata = FORMAT_METADATA[normalizedFormat];
  // Use real artwork tags when available, fall back to format-based metadata tags
  const displayTags = useMemo(() => {
    if (artwork.tags && artwork.tags.length > 0) return artwork.tags;
    return metadata.tags;
  }, [artwork.tags, metadata.tags]);
  const licenseLabel = artwork.isPremium ? "Premium" : artwork.isFree === false ? "Premium" : "Grátis";
  // Use real file size if available, otherwise use metadata default
  const fileSizeDisplay = artwork.fileSize ? formatFileSize(artwork.fileSize) : metadata.size;
  const oneTimePriceLabel = useMemo(() => {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(oneTimeCheckoutAmount);
  }, [oneTimeCheckoutAmount]);
  
  // Novo texto comercial oficial do Artes Turbo
  const description = useMemo(() => {
    return null; // Texto movido para JSX para melhor formatação visual
  }, []);

  // Download function (manual and automatic use)
  const triggerDownload = async ({ silent = false } = {}): Promise<boolean> => {
    if (downloading && !silent) return false;

    // Determine if artwork is premium
    const isArtworkPremium = artwork.isPremium || artwork.isFree === false;

    // Check permission before downloading
    setDownloading(true);
    try {
      const permission = await checkPermission(artwork.id, isArtworkPremium);

      if (!permission.canDownload) {
        if (permission.alreadyDownloaded) {
          // Already downloaded, allow re-download
          const { showToast } = await import('@/components/ui/Toast');
          showToast('ℹ️ Você já baixou esta arte. Re-download permitido!', 'info');
        } else if (permission.limitReached) {
          // Limit reached, show modal
          setLimitReachedData(permission.timeUntilReset);
          setLimitModalOpen(true);
          setDownloading(false);
          return false;
        } else if ((permission as any).redirectTo === '/planos' || (permission as any).registrationSource) {
          // Backend pediu redirect para planos: usar modal de assinatura
          const origin = (permission as any).registrationSource || user?.registrationSource || 'direct';
          setSubscriptionModalOrigin(origin as 'crie_oferta' | 'direct');
          setSubscriptionModalOpen(true);
          setDownloading(false);
          return false;
        } else {
          // Other permission error
          const { showToast } = await import('@/components/ui/Toast');
          showToast(permission.reason || 'Não foi possível verificar permissão de download', 'error');
          setDownloading(false);
          return false;
        }
      }

      const isFirstTimeDownload = !permission.alreadyDownloaded;
      const token = authHelpers.getToken?.() ?? null;

      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao baixar arquivo');
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${artwork.title}.${normalizedFormat.toLowerCase()}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Backend already records the download automatically
      // Refresh user data if this was a first-time download to update counters
      if (isFirstTimeDownload) {
        await refreshUser();
      }

      if (!silent) {
        const { showToast } = await import('@/components/ui/Toast');
        showToast('Download iniciado com sucesso! 🎉', 'success');
      }
      setDownloading(false);
      return true;
    } catch (error: any) {
      const { showToast } = await import('@/components/ui/Toast');
      if (silent) {
        showToast('Compra concluída, mas não foi possível iniciar o download automático. Baixe em "Meus downloads".', 'error');
      } else {
        showToast(error?.message || 'Erro ao baixar arquivo', 'error');
      }
      setDownloading(false);
      return false;
    }
  };

  const handleDownload = () => {
    if (!requireAuth()) return;
    void triggerDownload();
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const updateViewport = () => setIsMobile(mediaQuery.matches);
    updateViewport();
    const listener = (event: MediaQueryListEvent) => setIsMobile(event.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', listener);
    } else {
      mediaQuery.addListener(listener);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', listener);
      } else {
        mediaQuery.removeListener(listener);
      }
    };
  }, []);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const frame = window.requestAnimationFrame(() => setIsEntering(true));

    return () => {
      document.body.style.overflow = previousOverflow;
      window.cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    if (!isClosing) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      const nextArtwork = pendingArtworkRef.current;
      if (nextArtwork) {
        pendingArtworkRef.current = null;
        onSwitchArtwork(nextArtwork);
        setIsClosing(false);
        setIsEntering(true);
        return;
      }
      onClose();
    }, TRANSITION_DURATION);
    return () => window.clearTimeout(timeoutId);
  }, [isClosing, onClose, onSwitchArtwork]);

  // Calculate visible tags based on container width
  useEffect(() => {
    const calculateVisibleTags = () => {
      if (!tagsContainerRef.current || !isMobile) return;

      const containerWidth = tagsContainerRef.current.offsetWidth - 32; // subtract padding
      const tagGap = 8; // gap between tags
      const moreButtonWidth = 40; // approximate width of "+X" button

      let totalWidth = 0;
      let count = 0;

      // Create temporary elements to measure actual tag widths
      const tempContainer = document.createElement('div');
      tempContainer.style.visibility = 'hidden';
      tempContainer.style.position = 'absolute';
      document.body.appendChild(tempContainer);

      for (const tag of displayTags) {
        const tempTag = document.createElement('span');
        tempTag.className = 'rounded-full border border-brand-secondary/30 bg-brand-secondary/15 px-2 py-0.5 text-[10px] font-medium whitespace-nowrap';
        tempTag.textContent = `#${tag}`;
        tempContainer.appendChild(tempTag);

        const tagWidth = tempTag.offsetWidth;
        const spaceNeeded = totalWidth + tagWidth + (count > 0 ? tagGap : 0);

        // Check if adding this tag + more button would fit
        if (spaceNeeded + (displayTags.length > count + 1 ? moreButtonWidth + tagGap : 0) <= containerWidth) {
          totalWidth = spaceNeeded;
          count++;
        } else {
          break;
        }
      }

      document.body.removeChild(tempContainer);
      setVisibleTagsCount(Math.max(1, count));
    };

    calculateVisibleTags();
    window.addEventListener('resize', calculateVisibleTags);
    return () => window.removeEventListener('resize', calculateVisibleTags);
  }, [displayTags, isMobile]);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0 });
    }
  }, [artwork.id]);

  useEffect(() => {
    setHasPurchased(false);
    setIsCheckoutOpen(false);
    setCheckoutTarget(null);
  }, [artwork.id]);

  // Check if the current user already purchased this artwork (for one-time purchases)
  useEffect(() => {
    let mounted = true;

    const checkPurchased = async () => {
      try {
        if (!authHelpers.isAuthenticated()) return;

        const resp = await apiFetch<{ purchased?: boolean }>(
          apiRoutes.downloads.editorCheck(artwork.id),
          { method: 'GET' }
        );

        if (!mounted) return;

        if (resp && (resp as any).purchased) {
          setHasPurchased(true);
        } else {
          setHasPurchased(false);
        }
      } catch (err) {
        // Ignore errors (e.g., not authenticated or 403) and leave hasPurchased as false
        // but log for debugging
        // console.debug('[ArtworkModal] checkPurchased error', err);
      }
    };

    void checkPurchased();

    return () => { mounted = false; };
  }, [artwork.id]);

  const closeModal = useCallback(() => {
    pendingArtworkRef.current = null;
    setIsEntering(false);
    setIsClosing(true);
  }, []);

  const handleShare = useCallback(async () => {
    const shareUrl = `${window.location.origin}/arte/${artwork.id}`;

    // Try native share API on mobile
    if (navigator.share) {
      try {
        await navigator.share({ title: artwork.title, url: shareUrl });
        return;
      } catch {
        // User cancelled or API failed — fall through to clipboard
      }
    }

    // Copy to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      const { showToast } = await import('@/components/ui/Toast');
      showToast('Link copiado para a área de transferência! 🔗', 'success');
      setTimeout(() => setShareCopied(false), 2500);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setShareCopied(true);
      const { showToast } = await import('@/components/ui/Toast');
      showToast('Link copiado para a área de transferência! 🔗', 'success');
      setTimeout(() => setShareCopied(false), 2500);
    }
  }, [artwork.id, artwork.title]);

  const handleCarouselVisualize = useCallback((nextArtwork: ArtworkModalItem) => {
    if (nextArtwork.id === artwork.id || isClosing) {
      return;
    }
    pendingArtworkRef.current = nextArtwork;
    setIsEntering(false);
    setIsClosing(true);
  }, [artwork.id, isClosing]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeModal]);

  const stopPropagation = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  const handleOpenCheckout = async () => {
    if (!requireAuth()) return;

    // Fetch latest product price to ensure admin changes are reflected at checkout
    let amount = oneTimeCheckoutAmount;
    try {
      const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:4001/api";
      const resp = await fetch(`${API_URL}/public/products/single-artwork`);
      if (resp.ok) {
        const product = await resp.json();
        const parsed = Number(product.amount);
        if (!Number.isNaN(parsed)) amount = parsed;
      }
    } catch (err) {
      // ignore and use fallback
      console.warn('Failed to fetch latest product price for checkout', err);
    }

    setCheckoutTarget({
      kind: "one_time",
      productId: "single-artwork",
      productTitle: artwork.title,
      amount,
      currency: "BRL",
      artworkId: artwork.id
    } as any);
    setIsCheckoutOpen(true);
  };

  const handleCloseCheckout = () => {
    setIsCheckoutOpen(false);
    setCheckoutTarget(null);
  };

  const handleCheckoutSuccess = async () => {
    setHasPurchased(true);
    const wasDownloaded = await triggerDownload({ silent: true });
    const { showToast } = await import('@/components/ui/Toast');
    if (wasDownloaded) {
      showToast('Compra confirmada! Download liberado automaticamente.', 'success');
    } else {
      showToast('Compra confirmada! Faça o download quando quiser em "Meus downloads".', 'success');
    }
    handleCloseCheckout();
  };

  const autoplayConfig = isMobile
    ? false
    : { delay: 4200, disableOnInteraction: false, pauseOnMouseEnter: true } as const;

  const collectionsToShow = collections.slice(0, 5);

  const overlayClasses = clsx(
    "fixed inset-0 z-[1000] flex items-start justify-center overflow-y-auto bg-black/70 px-3 py-10 sm:px-6 transition-opacity duration-200 ease-out",
    isEntering && !isClosing ? "opacity-100" : "opacity-0"
  );

  const containerClasses = clsx(
    "artwork-modal-container relative flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden border border-white/10 bg-[#1E1B4B] text-white shadow-2xl shadow-black/50 transition-all duration-300 ease-out",
    isEntering && !isClosing ? "translate-y-0 scale-100 opacity-100" : "translate-y-8 scale-95 opacity-0"
  );

  return createPortal(
    <div className={overlayClasses} onClick={closeModal}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Pré-visualização da arte ${artwork.title}`}
        className={containerClasses}
        onClick={stopPropagation}
      >
        {/* Close Button - Floating outside on desktop if possible, or top right clean */}
        <button
          type="button"
          onClick={closeModal}
          className="absolute right-6 top-6 z-50 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition hover:bg-white/20 hover:scale-110"
          aria-label="Fechar modal"
        >
          <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
        </button>

        <div ref={contentRef} className="flex flex-1 flex-col overflow-y-auto custom-scrollbar">
          <div className="flex flex-col lg:flex-row">
            {/* Left Column: Image & Visuals */}
            <div className="relative flex flex-col bg-black/20 lg:w-[55%] lg:border-r lg:border-white/5">
              <div className="relative flex h-full min-h-[400px] items-center justify-center p-6 lg:p-10">
                {/* Main Image */}
                <div className="artwork-modal-image-container relative w-full overflow-hidden shadow-2xl ring-1 ring-white/10 transition-transform duration-500 hover:scale-[1.02]">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={artwork.image}
                    alt={`Pré-visualização da arte ${artwork.title}`}
                    className="w-full object-contain"
                    loading="eager"
                  />
                  {/* Watermark Overlay - Repeated Pattern */}
                  <div
                    className="pointer-events-none absolute inset-0 h-full w-full opacity-10"
                    style={{
                      backgroundImage: 'url(/images/marcadagua.png)',
                      backgroundRepeat: 'repeat',
                      backgroundSize: '500px',
                    }}
                    aria-hidden="true"
                  />
                </div>
              </div>

              {/* Image Toolbar (Desktop) */}
              <div className="hidden items-center justify-between border-t border-white/5 bg-[#0F011B]/30 px-8 py-4 lg:flex">
                <div className="flex items-center gap-4">
                  <button
                    onClick={toggleLike}
                    disabled={interactionsLoading}
                    className={clsx(
                      "group flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-all border",
                      isLiked 
                        ? "bg-red-500/10 border-red-500/50 text-red-400 hover:bg-red-500/20" 
                        : "bg-white/5 border-white/5 text-white hover:bg-white/10 hover:border-white/20"
                    )}
                  >
                    <FontAwesomeIcon icon={faHeart} className={clsx("h-4 w-4 transition-transform group-hover:scale-110", isLiked && "animate-pulse")} />
                    <span>{isLiked ? 'Curtido' : 'Curtir'}</span>
                  </button>
                  <button
                    onClick={toggleSave}
                    disabled={interactionsLoading}
                    className={clsx(
                      "group flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-all border",
                      isSaved 
                        ? "bg-brand-secondary/10 border-brand-secondary/50 text-brand-secondary hover:bg-brand-secondary/20" 
                        : "bg-white/5 border-white/5 text-white hover:bg-white/10 hover:border-white/20"
                    )}
                  >
                    <FontAwesomeIcon icon={faBookmark} className={clsx("h-4 w-4 transition-transform group-hover:scale-110", isSaved && "animate-pulse")} />
                    <span>{isSaved ? 'Salvo' : 'Salvar'}</span>
                  </button>
                </div>
                
                <div className="flex items-center gap-2">
                   <button
                      onClick={handleShare}
                      className={clsx(
                        "flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-all border",
                        shareCopied
                          ? "bg-brand-secondary/10 border-brand-secondary/50 text-brand-secondary"
                          : "bg-white/5 border-white/5 text-white hover:bg-white/10 hover:border-white/20"
                      )}
                   >
                      <FontAwesomeIcon icon={shareCopied ? faCheck : faShareNodes} className={clsx("h-4 w-4 transition-transform", shareCopied && "scale-110")} />
                      <span>{shareCopied ? 'Link copiado!' : 'Compartilhar'}</span>
                   </button>
                </div>
              </div>
            </div>

            {/* Right Column: Info & Actions */}
            <div className="flex flex-1 flex-col p-6 lg:p-10">
              <div className="flex flex-col gap-6">
                {/* Header */}
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-3">
                    {artwork.isPremium ? (
                       <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-gradient px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg shadow-brand-primary/20">
                         <FontAwesomeIcon icon={faCrown} className="h-3 w-3" />
                         Premium
                       </span>
                    ) : (
                       <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-secondary/20 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-brand-secondary border border-brand-secondary/30">
                         <FontAwesomeIcon icon={faGift} className="h-3 w-3" />
                         Grátis
                       </span>
                    )}
                  </div>
                  
                  <h3 className="text-3xl font-bold leading-tight text-white lg:text-4xl">
                    {artwork.title}
                  </h3>
                </div>

                {/* Texto Comercial Artes Turbo */}
                <div className="space-y-5 rounded-2xl bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-8 border border-white/10 backdrop-blur-sm">
                  <div className="space-y-5 text-[15px] leading-relaxed text-slate-300">
                    <p className="text-white">
                      Aqui você não compra uma arte.
                      <br />
                      Você ativa uma máquina de vendas.
                    </p>
                    
                    <p>
                      Você pode comprar o arquivo avulso ou entrar no modo assinatura.
                      <br />
                      Na assinatura, edita o mesmo modelo quantas vezes quiser, durante todo o período, com uso comercial incluso.
                    </p>
                    
                    <p>
                      Suas cores, sua marca e seus dados entram automaticamente.
                      <br />
                      Sem programas, sem complicação, sem depender de ninguém.
                    </p>
                    
                    <p className="text-white">
                      Você clica.
                      <br />
                      A arte nasce pronta.
                      <br />
                      É só publicar. <FontAwesomeIcon icon={faRocket} className="inline h-4 w-4" />
                    </p>
                  </div>

                  {/* Info Técnica */}
                  <div className="flex items-center gap-6 pt-3 mt-5 border-t border-white/5 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-brand-secondary" />
                      <span className="text-white/60">Licença</span>
                      <span className="font-semibold text-white">{licenseLabel}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-brand-secondary" />
                      <span className="text-white/60">Qualidade</span>
                      <span className="font-semibold text-white">{metadata.qualityLabel}</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-3 pt-2">
                  {/* Download Button - Temporarily disabled
                      Motivo: Desativado temporariamente em 2025-12-19 para manutenção/ajustes.
                      Para reativar, remova o comentário abaixo e ajuste conforme necessário.

                  <button
                    type="button"
                    onClick={handleDownload}
                    disabled={downloading}
                    className="group relative flex w-full items-center justify-center gap-3 rounded-xl bg-brand-secondary px-6 py-4 text-base font-bold text-brand-dark shadow-[0_0_20px_-5px_rgba(34,197,94,0.4)] transition-all hover:scale-[1.02] hover:shadow-[0_0_30px_-5px_rgba(34,197,94,0.6)] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FontAwesomeIcon icon={faDownload} className={clsx("h-5 w-5 transition-transform group-hover:-translate-y-1", downloading && "animate-bounce")} />
                    {downloading ? 'Baixando...' : 'Baixar Arquivo'}
                  </button>
                  */}

                  {/* Edit with 1 Click Button */}
                  {normalizedFormat === "PSD" && (
                    <div className="relative">
                      <button
                        type="button"
                        onClick={async () => {
                          if (!requireAuth()) return;

                          try {
                            // Create secure editor session (server validates permission, records consumption, sets httpOnly cookie)
                            const resp = await apiFetch<{ 
                              success?: boolean; 
                              expiresAt?: string;
                              error?: string;
                              limitReached?: boolean; 
                              redirectTo?: string;
                              registrationSource?: string;
                            }>(
                              apiRoutes.editor.createSession(),
                              {
                                method: 'POST',
                                body: JSON.stringify({ artworkId: artwork.id }),
                                credentials: 'include' // Ensure cookies are sent/received
                              }
                            );

                            if (resp && resp.success) {
                              // Close modal and navigate to secure editor page
                              onClose?.();
                              
                              // Store editor preferences in sessionStorage for the editor page to use
                              if (typeof window !== 'undefined') {
                                sessionStorage.setItem('editorPreferences', JSON.stringify({
                                  autoApplyBrandColors: applyBrandColorsOnOpen
                                }));
                                // Development-only: store sessionId as fallback for embedded browsers
                                // that may not send httpOnly cookies (VS Code Simple Browser)
                                if (process.env.NODE_ENV !== 'production' && (resp as any).sessionId) {
                                  sessionStorage.setItem('editorSessionId', (resp as any).sessionId);
                                }
                              }

                              // Navigate to the secure editor page (no sensitive params in URL)
                              router.push('/editor');
                            } else {
                              // Handle error response
                              if (resp?.limitReached || resp?.redirectTo) {
                                // Abrir modal de assinatura ao invés de redirecionar direto.
                                // Prioriza registrationSource vindo da resposta para evitar
                                // JWT/contexto desatualizado quando o usuário acabou de chegar via SSO.
                                const origin = resp?.registrationSource || user?.registrationSource || 'direct';
                                setSubscriptionModalOrigin(origin as 'crie_oferta' | 'direct');
                                setSubscriptionModalOpen(true);
                              } else if (resp?.error) {
                                const { showToast } = await import('@/components/ui/Toast');
                                showToast(resp.error, 'error');
                              }
                            }
                          } catch (err: any) {
                            // If server returned 403 with limit info, open subscription modal
                            if (err?.status === 403 && err?.data) {
                              const origin = err.data.registrationSource || user?.registrationSource || 'direct';
                              setSubscriptionModalOrigin(origin as 'crie_oferta' | 'direct');
                              setSubscriptionModalOpen(true);
                              return;
                            }

                            const { showToast } = await import('@/components/ui/Toast');
                            showToast(err?.message || 'Erro ao abrir editor', 'error');
                          }
                        }}
                        className="group flex w-full items-center justify-start gap-3 rounded-xl bg-brand-primary px-6 py-4 text-base font-bold text-white shadow-lg shadow-brand-primary/20 transition-all hover:bg-brand-primary/90 hover:scale-[1.02] hover:shadow-brand-primary/40 active:scale-[0.98] sm:justify-center"
                      >
                        {!isMobile && (
                          <FontAwesomeIcon icon={faWandMagicSparkles} className="h-5 w-5" />
                        )}
                        Editar com 1 Clique
                      </button>
                      <div 
                        className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2 rounded-full bg-black/30 px-2 py-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Switch
                          checked={applyBrandColorsOnOpen}
                          onChange={handleToggleBrandColor}
                          label="Aplicar cor da marca"
                          variant="status"
                        />
                        <span className="text-xs font-medium text-white/80">COR</span>
                      </div>
                    </div>
                  )}

                  {!hasPurchased && !artwork.isFree && !hasActiveSubscription && (
                    <button
                      type="button"
                      onClick={handleOpenCheckout}
                      className="group mt-2 flex w-full items-center justify-center gap-3 rounded-xl border-2 border-transparent bg-gradient-to-r from-amber-400 to-amber-500 px-6 py-4 text-[15px] font-bold text-[#1E1B4B] shadow-[0_8px_30px_-8px_rgba(212,175,55,0.45)] transition-all hover:from-amber-500 hover:to-amber-600 hover:shadow-[0_10px_40px_-12px_rgba(212,175,55,0.55)]"
                    >
                      <FontAwesomeIcon icon={faCartShopping} className="h-5 w-5 transition-transform group-hover:scale-110" />
                      {`Comprar essa arte por ${oneTimePriceLabel}`}
                    </button>
                  )}
                  {hasPurchased && !artwork.isFree && (
                    <div
                      className="group mt-2 flex w-full items-center justify-center gap-3 rounded-xl bg-emerald-500 px-6 py-4 text-[15px] font-bold text-white shadow-[0_8px_30px_-8px_rgba(34,197,94,0.25)] transition-all"
                      role="status"
                      aria-label="Você comprou essa arte"
                    >
                      <FontAwesomeIcon icon={faCheckCircle} className="h-5 w-5" />
                      Você comprou essa arte
                    </div>
                  )}
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 pt-4">
                  {displayTags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => {
                        onClose();
                        router.push(`/busca?q=${encodeURIComponent(tag)}`);
                      }}
                      className="cursor-pointer rounded-lg bg-white/5 px-3 py-1.5 text-xs font-medium text-white/70 transition-colors hover:bg-brand-secondary/15 hover:text-brand-secondary"
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 border-t border-white/10 px-5 py-6 sm:px-8">
            <div className="space-y-8">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold text-white">
                    {artwork.collectionSlug && collectionArtworks.length > 0 ? 'Artes da mesma coleção' : 'Outras Artes'}
                  </h4>
                </div>
                <Swiper
                  modules={isMobile ? [Navigation, A11y] : [Navigation, Autoplay, A11y]}
                  navigation
                  {...(isMobile ? {} : { autoplay: { delay: 4200, disableOnInteraction: true, pauseOnMouseEnter: true } })}
                  spaceBetween={12}
                  slidesPerView="auto"
                >
                  {/* Use collection artworks if available, otherwise use related artworks */}
                  {(artwork.collectionSlug && collectionArtworks.length > 0 ? collectionArtworks : relatedArtworks).map((item) => (
                    <SwiperSlide key={`related-${item.id}`} className="!w-auto !h-auto">
                      <article 
                        className="group relative overflow-hidden rounded-xl shadow-lg shadow-black/20 cursor-pointer transition-all duration-300 hover:shadow-xl hover:shadow-black/30 hover:-translate-y-1"
                        onClick={() => handleCarouselVisualize(item)}
                      >
                        {/* Badge de favorito no canto superior direito */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Toggle save action could be added here
                          }}
                          className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-slate-800/80 text-white/70 backdrop-blur-sm transition-all hover:bg-slate-700 hover:text-brand-secondary"
                          aria-label="Salvar arte"
                        >
                          <FontAwesomeIcon icon={faBookmark} className="h-3.5 w-3.5" />
                        </button>
                        
                        {/* Imagem com altura fixa e largura automática baseada na proporção */}
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={item.image}
                          alt={`Arte relacionada ${item.title}`}
                          className="h-40 sm:h-48 w-auto object-cover transition duration-300 group-hover:scale-[1.03]"
                          loading="lazy"
                        />
                      </article>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold text-white">Coleções para você</h4>
                </div>
                <Swiper
                  modules={isMobile ? [Navigation, A11y] : [Navigation, Autoplay, A11y]}
                  navigation
                  {...(isMobile ? {} : { autoplay: { delay: 5200, disableOnInteraction: true, pauseOnMouseEnter: true } })}
                  spaceBetween={12}
                  slidesPerView={1.3}
                  breakpoints={{
                    640: { slidesPerView: 2 },
                    900: { slidesPerView: 2.5 },
                    1200: { slidesPerView: 3.5 }
                  }}
                >
                  {collectionsToShow.map((collection) => (
                    <SwiperSlide key={`collection-${collection.id}`} className="!h-auto">
                      <article 
                        className="group relative h-full w-full max-w-[280px] overflow-hidden rounded-2xl border border-white/10 bg-[#16032f] cursor-pointer"
                        onClick={() => {
                          onClose();
                          router.push(`/colecoes/${collection.slug}`);
                        }}
                      >
                        <div className="relative overflow-hidden bg-white/5 p-1">
                          <div className="grid grid-cols-2 gap-0.5">
                            {collection.previews.slice(0, 4).map((preview, index) => (
                              <div
                                key={`${collection.id}-preview-${index}`}
                                className="relative aspect-square overflow-hidden rounded-md"
                              >
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                  src={preview}
                                  alt={`Prévia da coleção ${collection.title}`}
                                  className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
                                  loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-transparent to-black/35 opacity-0 transition-opacity duration-300 group-hover:opacity-70" />
                              </div>
                            ))}
                          </div>

                          {/* Overlay com informações */}
                          <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/90 via-black/50 to-transparent p-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-start justify-between gap-2">
                                <h5 className="text-xs font-semibold text-white line-clamp-2">{collection.title}</h5>
                                <span className="rounded-full border border-white/20 bg-white/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white/90 whitespace-nowrap">
                                  {collection.assetsCount} assets
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {collection.tags.slice(0, 3).map((tag) => (
                                  <span
                                    key={`${collection.id}-${tag}`}
                                    className="rounded-full border border-brand-secondary/40 bg-brand-secondary/20 px-2 py-0.5 text-[9px] font-medium text-brand-secondary"
                                  >
                                    #{tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </article>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>
          </div>
        </div>
      </div>
      <UnifiedCheckoutModal
        isOpen={isCheckoutOpen}
        target={checkoutTarget}
        onClose={handleCloseCheckout}
        onSuccess={handleCheckoutSuccess}
        pixOnly
      />
      <DownloadLimitReachedModal
        isOpen={limitModalOpen}
        onClose={() => setLimitModalOpen(false)}
        timeUntilReset={limitReachedData}
        redirectToPlans={true}
        artworkId={artwork?.id ?? null}
      />
      <SubscriptionRequiredModal
        isOpen={subscriptionModalOpen}
        onClose={() => setSubscriptionModalOpen(false)}
        forceOrigin={subscriptionModalOrigin}
        artworkId={artwork?.id ?? null}
      />
    </div>, document.body
  );
}
